// Greyscale and tone, as one lookup table.
//
// A fax's real failure mode is not noise, it is EVERYTHING GOING SOLID BLACK.
// A photo dropped straight into a 1-bit threshold loses every shadow, and a
// face becomes a silhouette. That is not funny, it is just broken — the joke
// only lands if you can still tell who it is.
//
// So the tone curve does four things, composed into a single 256-entry table so
// the per-pixel loop has no branches and no maths:
//
//   1. sRGB -> linear -> back, so error diffusion works in a space where
//      "half grey" means half the light, not half the byte value
//   2. an S-curve, because a fax has no midtones and pretending otherwise
//      wastes the few levels a dither can represent
//   3. gamma 0.9, a small overall lift
//   4. a black-point lift, which is the one that saves faces
//
// Building the table costs 256 iterations once. Applying it costs one array
// read per pixel.

import { MID } from './geometry.js';

const srgbToLinear = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const linearToSrgb = (c) => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055);

/**
 * @param {object} [opts]
 * @param {number} [opts.contrast]   S-curve strength. 0 = none.
 * @param {number} [opts.blackLift]  How far off pure black the darkest ink sits.
 * @param {number} [opts.gamma]
 * @param {number} [opts.toner]      0..1. A low cartridge lightens everything.
 */
export function buildLut({ contrast = 0.25, blackLift = 0.08, gamma = 0.9, toner = 1 } = {}) {
  const lut = new Uint8Array(256);
  for (let i = 0; i < 256; i += 1) {
    let v = linearToSrgb(srgbToLinear(i / 255));

    // S-curve around the midpoint. smoothstep, mixed in by `contrast`.
    const s = v * v * (3 - 2 * v);
    v = v + (s - v) * contrast;

    v = v ** gamma;

    // Lift the black point. Without this a dark photo is a rectangle of ink.
    v = blackLift + v * (1 - blackLift);

    // Toner: as the cartridge empties the page goes pale, never darker.
    if (toner < 1) v = 1 - (1 - v) * (0.35 + 0.65 * toner);

    lut[i] = Math.max(0, Math.min(255, Math.round(v * 255)));
  }
  return lut;
}

/** Rec.709 luma. Matches how a human weights the channels; ITU-R BT.709. */
export function toGrey(rgba, w, h, lut) {
  const out = new Uint8ClampedArray(w * h);
  for (let i = 0, p = 0; i < out.length; i += 1, p += 4) {
    const y = (rgba[p] * 0.2126 + rgba[p + 1] * 0.7152 + rgba[p + 2] * 0.0722) | 0;
    out[i] = lut[y];
  }
  return out;
}

/**
 * 3x3 unsharp mask.
 *
 * The single highest-payoff step for photo legibility. Dithering destroys
 * gradients but preserves edges, so sharpening BEFORE the dither is what keeps
 * an eye an eye instead of a smudge. Blended rather than applied outright,
 * because at full strength it haloes.
 */
export function unsharp(grey, w, h, amount = 0.4) {
  if (amount <= 0) return grey;
  const out = new Uint8ClampedArray(grey.length);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1) { out[i] = grey[i]; continue; }
      const sharp = 5 * grey[i]
        - grey[i - 1] - grey[i + 1] - grey[i - w] - grey[i + w];
      out[i] = grey[i] + (sharp - grey[i]) * amount;
    }
  }
  return out;
}

export { MID };
